/**
 * Process Stepper - Custom element for interactive process section
 * Handles step navigation on desktop (click) and mobile (prev/next arrows)
 */
class ProcessStepper extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
  }

  connectedCallback() {
    this.steps = this.querySelectorAll('[data-step]');
    this.contents = this.querySelectorAll('[data-step-content]');
    this.progressFill = this.querySelector('.process__progress-fill');
    this.progressTrack = this.querySelector('.process__progress-track');
    this.currentNumber = this.querySelector('.process__current-number');
    this.prevBtn = this.querySelector('[data-action="prev"]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.totalSteps = this.contents.length;

    if (this.totalSteps === 0) return;

    this.bindEvents();
    this.goToStep(1);
    this.updateProgressTrackWidth();

    // Handle window resize for progress track width
    this.resizeObserver = new ResizeObserver(() => {
      this.updateProgressTrackWidth();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  bindEvents() {
    // Desktop: click on step numbers
    this.steps.forEach((step) => {
      step.addEventListener('click', () => {
        const stepNum = parseInt(step.dataset.step, 10);
        this.goToStep(stepNum);
      });
    });

    // Mobile: prev/next arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goToStep(this.currentStep - 1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goToStep(this.currentStep + 1);
      });
    }

    // Keyboard navigation when focused
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.goToStep(this.currentStep - 1);
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.goToStep(this.currentStep + 1);
      }
    });
  }

  goToStep(stepNum) {
    // Clamp to valid range
    stepNum = Math.max(1, Math.min(stepNum, this.totalSteps));

    // Skip if already on this step
    if (stepNum === this.currentStep && this.initialized) return;

    this.currentStep = stepNum;
    this.initialized = true;

    // Update progress line
    this.updateProgress();

    // Update step states
    this.steps.forEach((step) => {
      const num = parseInt(step.dataset.step, 10);
      step.classList.toggle('process__step--active', num === stepNum);
      step.classList.toggle('process__step--completed', num < stepNum);
      step.setAttribute('aria-current', num === stepNum ? 'step' : 'false');
    });

    // Update mobile current step display
    if (this.currentNumber) {
      this.currentNumber.textContent = stepNum.toString().padStart(2, '0');
    }

    // Update arrow states
    if (this.prevBtn) {
      this.prevBtn.disabled = stepNum === 1;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = stepNum === this.totalSteps;
    }

    // Show/hide content with animation
    this.contents.forEach((content, i) => {
      const isActive = i + 1 === stepNum;
      content.hidden = !isActive;
    });
  }

  updateProgress() {
    if (!this.progressFill || this.totalSteps <= 1) return;

    // Calculate progress percentage (0% at step 1, 100% at last step)
    const progress = ((this.currentStep - 1) / (this.totalSteps - 1)) * 100;
    this.progressFill.style.width = `${progress}%`;
  }

  updateProgressTrackWidth() {
    // Set progress track width to span from first to last step dot
    if (!this.progressTrack || this.steps.length < 2) return;

    const firstStep = this.steps[0];
    const lastStep = this.steps[this.steps.length - 1];

    if (!firstStep || !lastStep) return;

    const containerRect = this.progressTrack.parentElement.getBoundingClientRect();
    const firstRect = firstStep.getBoundingClientRect();
    const lastRect = lastStep.getBoundingClientRect();

    // Calculate width from center of first dot to center of last dot
    const width = lastRect.left + lastRect.width / 2 - (firstRect.left + firstRect.width / 2);
    const left = firstRect.left + firstRect.width / 2 - containerRect.left;

    this.progressTrack.style.width = `${width}px`;
    this.progressTrack.style.left = `${left}px`;
    this.progressTrack.style.transform = 'none';
  }
}

if (!customElements.get('process-stepper')) {
  customElements.define('process-stepper', ProcessStepper);
}
